import { Injectable } from '@angular/core';
import { ChartDefinitionBase } from './chart-definition-base';
import { IMachine } from '../api/domain-model';
import { MachineService } from '../api/machine.service';
import { NotificationService } from '../shared/notifications/notification.service';
import { ChartConfigService } from '../company/chart-config.service';
import { AuthService } from '../shared/auth-service';
import { TranslateService } from '../shared/translate';
import {
	ICollectiveFaultsTableCardDefinition,
	IGaugeCardDefinition,
	IInterruptsTableCardDefinition,
	INotPlacedTableCardDefinition,
	ISimpleContentCardDefinition,
	ISpoutTableCardDefinition,
	IWeightDataTableCardDefinition
} from '../charts/definition-model';
import CardDisplayVariables from '../common/domain/card-display-variables';
import { Identifiers, Util } from '../common/domain/bindings';

@Injectable()
export class ChartDefinitionIvtService extends ChartDefinitionBase {

	private machine: IMachine;

	constructor(
		public machineService: MachineService,
		public notificationService: NotificationService,
		public chartConfigService: ChartConfigService,
		public authService: AuthService,
		public translateService: TranslateService
	) {
		super();
	}

	setMachine(machine: IMachine): void {
		this.machine = machine;
	}

	getMachine(): IMachine {
		return this.machine;
	}

	getSortChangeTodayCardDefinition(): ISimpleContentCardDefinition {
		const requiredVariables = ['u16SortChgDay', 'u16SortChgBatch'];
		return <ISimpleContentCardDefinition>{
			neededValues: [requiredVariables],
			historyVariables: requiredVariables,
			title: 'A249SortChangeToday',
			cardBatchTitle: 'A249SortChangeTodayBatch',
			unit: 'A020Count',
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().typeChangesToday,
		}
	}

	getEmptyBagMagazineTodayCardDefinition(): ISimpleContentCardDefinition {
		const requiredVariables = ['u8ReloadBagMagazine', 'u8ReloadBagMagazineBatch'];
		return <ISimpleContentCardDefinition>{
			neededValues: [requiredVariables],
			historyVariables: requiredVariables,
			title: 'A250EmptyBagMagazineToday',
			cardBatchTitle: 'A250EmptyBagMagazineTodayBatch',
			unit: 'A020Count',
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().newReloadEmptyBagMagazineToday,
		}
	}

	getRejectionsTodayCardDefinition(): ISimpleContentCardDefinition {
		const requiredVariables = ['u32RejectCnt', 'u32RejectCntBatch'];
		return <ISimpleContentCardDefinition>{
			neededValues: [requiredVariables],
			historyVariables: requiredVariables,
			title: 'A028RejectionsToday',
			cardBatchTitle: 'A028RejectionsTodayBatch',
			unit: 'A020Count',
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().rejectsToday,
		}
	}

	getProductionChartDefinition(): IGaugeCardDefinition {
		return <IGaugeCardDefinition>{
			chartOptions: this.chartConfigService.anyGaugeChartSpec(
				this.machine,
				'produktionGutsaeckeSpec',
				0,
				100,
				Identifiers.ivtGoodProductionCard,
				null
			).options,
			title: 'A129BagManufacture',
			unit: 'A027Percent',
			historyVariables: Identifiers.ivtGoodProductionCard[2],
			colorHistoryVariables: Identifiers.ivtGoodProductionCard,
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().goodProduction
		};
	}

	getSpoutCardDefinition(): ISpoutTableCardDefinition {
		const requiredVariables = Identifiers.spoutCard;
		return {
			neededValues: requiredVariables,
			historyVariables: ['u32SpoutProdCnt'],
			title: 'A023BagCounterPerFS',
			cardBatchTitle: 'A023BagCounterPerFSBatch',
			unit: 'A016Units',
			labelPrefix: 'A024FillingStation',
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().bagCounterToday,
		}
	}

	getInterruptsCardDefinition(): IInterruptsTableCardDefinition {
		const requiredVariables = [
			...Identifiers.ivtInterruptCard.countParams,
			...Identifiers.ivtInterruptCard.timeParams
		];
		return {
			neededValues: requiredVariables,
			historyVariables: ['u16StopFilterCnt'],
			title: 'A284Interrupts',
			cardBatchTitle: 'A284InterruptsBatch',
			unit: 'A124Minutes',
			machineType: '',
			countParams: Identifiers.ivtInterruptCard.countParams,
			timeParams: Identifiers.ivtInterruptCard.timeParams,
			titleParams: Identifiers.ivtInterruptCard.titleParams,
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().interruptionsToday,
			historyNeededValues: Util.getHistoryNeededValues(Identifiers.ivtInterruptCard.timeParams, Identifiers.ivtInterruptCard.countParams, Identifiers.ivtInterruptCard.titleParams)
		}
	}

	getWeightFillingStationDataCardDefinition(): IWeightDataTableCardDefinition {
		const requiredVariables = Identifiers.fillingStationWeightCard;
		return {
			neededValues: requiredVariables,
			historyVariables: ['s32MeanNetScale'],
			netScalePrefix: 's32MeanNetScale',
			netWeightsPrefix: 's32MeanWeights',
			decimals: 's16MeanDecimals',
			title: 'A422WeightData',
			cardBatchTitle: 'A422WeightData',
			unit: 's16MeanUnit',
			multilineChartSpec: this.chartConfigService.fillingStationNetWeighterMultiLineChartConfig(
				Identifiers.fillingStationWeightCard,
				['u8NetWeighersCnt', 'u8SpoutCnt']
			),
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().weightData,
		}
	}

	getStandardDeviationDataCardDefinition(): IWeightDataTableCardDefinition {
		const requiredVariables = Identifiers.fillingStationStandardDeviationCard;
		return {
			neededValues: requiredVariables,
			historyVariables: ['s32StdDevNetScale'],
			netScalePrefix: 's32StdDevNetScale',
			netWeightsPrefix: 's32StdDev',
			decimals: 's16StdDevDecimals',
			title: 'A423StdDevData',
			cardBatchTitle: 'A423StdDevData',
			unit: 's16StdDevUnit',
			multilineChartSpec: this.chartConfigService.fillingStationNetWeighterMultiLineChartConfig(
				Identifiers.fillingStationStandardDeviationCard,
				['u8NetWeighersCnt', 'u8SpoutCnt']
			),
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().stdDevData,
		}
	}

	getNotPlacedCardDefinition(): INotPlacedTableCardDefinition {
		const requiredVariables = Identifiers.notPlacedCard;
		return {
			neededValues: requiredVariables,
			historyVariables: ['u32SpoutPlacedCnt'],
			title: 'A234NotPlacedBags',
			cardBatchTitle: 'A234NotPlacedBags',
			unit: 'A020Count',
			labelPrefix: 'A024FillingStation',
			stackedAreaChartSpec: this.chartConfigService.bagCounterStackedAreaChartConfig(
				Identifiers.ivtNotPlacedBagsCards,
				'u8SpoutCnt',
				'A016Units'
			),
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().notPlacedBags,
		}
	}

	getCollectiveFaultsCardDefinition(fillingStationCount: number): ICollectiveFaultsTableCardDefinition {
		const requiredVariables = Identifiers.collectiveFaultsCard(fillingStationCount);
		const aliases: Array<Array<string>> = Identifiers.collectiveFaultsCard(fillingStationCount);
		const countVariables = aliases.shift(); // remove cnt variable
		const multilineChartSpec = this.chartConfigService.getCollectiveFaultsMultiLineChartConfig('A024FillingStation', countVariables, aliases, fillingStationCount);

		return {
			neededValues: requiredVariables,
			neededValuesSplitted: Identifiers.collectiveFaultsCardSplitted(fillingStationCount),
			historyVariables: ['u16FaultGeneralSpout'],
			title: 'A235CollectiveFaultsToday',
			cardBatchTitle: 'A235CollectiveFaultsTodayBatch',
			unit: 'A124Minutes',
			placerCount: ['u16FaultGeneralCnt', 'u16FaultGeneralCntBatch'],
			placerTime: ['u16FaultGeneralTime', 'u16FaultGeneralTimeBatch'],
			multilineChartSpec,
			displayVariable: CardDisplayVariables.ivtCardDisplayVariables().collectiveFaultsToday,
			historyNeededValues: Util.getHistoryNeededValues(Identifiers.collectiveFaultsCardSplitted(fillingStationCount).timeParams, Identifiers.collectiveFaultsCardSplitted(fillingStationCount).countParams, Identifiers.collectiveFaultsCardSplitted(fillingStationCount).titleParams)
		}
	}
}
